import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import { fetchVendorProfile } from "../../../core/utils/vendorUtils";

import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from '@mui/icons-material/Warning';
import { red } from '@mui/material/colors';
import IconButton from "@mui/material/IconButton";
import Cancel from '@mui/icons-material/Cancel';
import Swal from "sweetalert2";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDButton from "components/MDButton";
import LogoutButton from "layouts/logout";
import { useNavigate } from "react-router-dom";
import { AccessTimeRounded, Block, CheckBoxRounded, LocalShipping, Download } from "@mui/icons-material";
import CircularProgress from '@mui/material/CircularProgress';
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, FormControl, InputLabel, Select, MenuItem, Paper } from '@mui/material';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { ServerBaseUrlForStaticImages } from "constants";


// --- New Constants for Billing Screen UI Enhancements ---

// --- End New Constants ---


// Columns definition for DataTable.
// Accessors are now functions to ensure custom formatting is always applied.
const columns = [
  {
    Header: "S.No.",
    accessor: "s_no",
    width: "5%",
  },
  {
    Header: "Customer Name",
    accessor: "customer_name",
    sortable: true,
  },
  {
    Header: "Order Date",
    // Use a function for accessor to apply formatDateForDisplay directly
    accessor: row => {
      const formatDateForDisplay = (dateString) => {
        if (!dateString) return "N/A";
        try {
          const dateOnlyPart = dateString.split(',')[0].trim();
          const parts = dateOnlyPart.split('-');
          if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
            return `${parts[0]}-${parts[1]}-${parts[2]}`;
          }
          const dateObj = new Date(dateString);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
          }
        } catch (error) {
          console.error("Error formatting date for display in accessor:", dateString, error);
        }
        return "N/A";
      };
      return formatDateForDisplay(row.order_date);
    },
    sortable: true,
  },
  {
    Header: "Order Items",
    // Use a function for accessor to parse and display product names only
    accessor: row => {
      const parseOrderItems = (orderItemsData) => {
        let parsedItems = [];
        try {
          if (typeof orderItemsData === 'string') {
            parsedItems = JSON.parse(orderItemsData);
          } else if (Array.isArray(orderItemsData)) {
            parsedItems = orderItemsData;
          } else if (typeof orderItemsData === 'object' && orderItemsData !== null) {
            parsedItems = [orderItemsData];
          }
        } catch (err) {
          console.error("Error parsing order items in accessor:", err);
          parsedItems = [];
        }
        return parsedItems.filter(item => item && typeof item === 'object' && item.product_name);
      };

      const parsedItems = parseOrderItems(row.order_items);
      return parsedItems.map(item => item.product_name).join(", ");
    },
    sortable: true,
  },
  {
    Header: "Order Status",
    accessor: "order_status",
    sortable: true,
  },
  {
    Header: "Options",
    accessor: "option", // Still handled by custom component render in the data mapping
  },
];


function OrderDetails() {
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openContentModal, setOpenContentModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [customerMessage, setCustomerMessage] = useState("");
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [courierReceipt, setCourierReceipt] = useState(null);
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [podNumber, setPodNumber] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [deliveredBy, setDeliveredBy] = useState("");
  const [openZoomModal, setOpenZoomModal] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState('');
  const [deliveryDate, setDeliveryDate] = useState("");
  const [warrantyNumber, setWarrantyNumber] = useState('');
  const [printSeal, setPrintSeal] = useState('');

  const [errors, setErrors] = useState({
    podNumber: "",
    deliveryDate: "",
    deliveredBy: ""
  });

  const navigate = useNavigate();

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [vendorDetails, setVendorDetails] = useState({
    company_name: "",
    company_address: "",
    company_email: "",
    company_gst_no: "",
    company_phone_no: "",
  });

  // Helper function to format date consistently to DD-MM-YYYY (without time)
  // This is used for display elements other than the DataTable itself (which uses accessor functions)
  const formatDateForDisplay = useCallback((dateString) => {
    if (!dateString) return "N/A";
    try {
      const dateOnlyPart = dateString.split(',')[0].trim();
      const parts = dateOnlyPart.split('-');
      if (parts.length === 3 && parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        return `${parts[0]}-${parts[1]}-${parts[2]}`;
      }
      const dateObj = new Date(dateString);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
      }
    } catch (error) {
      console.error("Error formatting date for display:", dateString, error);
    }
    return "N/A";
  }, []);

  // Helper function to parse order items safely
  // This is used for logic and rendering within the modal, not directly by DataTable's accessor string
  const parseOrderItems = useCallback((orderItemsData) => {
    let parsedItems = [];
    try {
      if (typeof orderItemsData === 'string') {
        parsedItems = JSON.parse(orderItemsData);
      } else if (Array.isArray(orderItemsData)) {
        parsedItems = orderItemsData;
      } else if (typeof orderItemsData === 'object' && orderItemsData !== null) {
        parsedItems = [orderItemsData];
      }
    } catch (err) {
      console.error("Error parsing order items:", err);
      parsedItems = [];
    }
    return parsedItems.filter(item => item && typeof item === 'object' && item.product_name);
  }, []);

  useEffect(() => {
    const email = localStorage.getItem("email");
    if (email) {
      fetchVendorProfile(email, (vendor) => {
        setVendorDetails({
          company_name: vendor.businessName || "",
          company_address: vendor.branchName || "",
          company_email: vendor.email || "",
          company_gst_no: vendor.gst || "",
          company_phone_no: vendor.mobileNumber || "",
          signature_photo: vendor.signaturePhoto || null,

          // ⭐ NEW FIELD: LOGO FIX
          logo_photo:
            vendor.logo_photo ||
            vendor.logo ||
            vendor.vendor_logo ||
            null,
        });
      });
    }
  }, []);


  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const email = localStorage.getItem("email");
      const res = await api.get("/order_details/fetch", {
        params: { email }
      });

      const fetchedOrders = res.data || [];

      fetchedOrders.sort((a, b) => {
        const parseDateForSorting = (dateStr) => {
          if (!dateStr) return null;
          const datePart = dateStr.split(',')[0].trim();
          const parts = datePart.split('-');
          if (parts.length === 3) {
            return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }
          return new Date(dateStr);
        };

        const dateA = parseDateForSorting(a.order_date);
        const dateB = parseDateForSorting(b.order_date);

        if (!dateA || !dateB) return 0;
        return dateB.getTime() - dateA.getTime();
      });

      setOrders(fetchedOrders);
      // Reset page to 0 if the current page becomes out of bounds
      if (page * rowsPerPage >= fetchedOrders.length && fetchedOrders.length > 0) {
        setPage(0);
      } else if (fetchedOrders.length === 0) {
        setPage(0);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setSnackbar({ open: true, message: "Failed to fetch orders.", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]); // Added page and rowsPerPage to dependencies

  useEffect(() => {
    disableBackNavigation();
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    if (selectedRow) {
      setOrderStatus(selectedRow.order_status || "Pending");
      setPaymentStatus(selectedRow.payment_status || "Unpaid");
      setPodNumber(selectedRow.pod_number || "");
      setDeliveredBy(selectedRow.delivered_by || "");
      setCourierReceipt(null);

      if (selectedRow.delivery_date) {
        let formattedDateForInput = "";
        try {
          const datePart = selectedRow.delivery_date.split(',')[0].trim();
          const parts = datePart.split('-');
          if (parts.length === 3) {
            formattedDateForInput = `${parts[2]}-${parts[1]}-${parts[0]}`;
          } else {
            const dateObject = new Date(selectedRow.delivery_date);
            if (!isNaN(dateObject.getTime())) {
              formattedDateForInput = dateObject.toISOString().split("T")[0];
            }
          }
        } catch (err) {
          console.error("Error parsing delivery_date for input:", selectedRow.delivery_date, err);
        }
        setDeliveryDate(formattedDateForInput || "");
      } else {
        setDeliveryDate("");
      }
    }
  }, [selectedRow]);


  const handleUpdateOrder = async () => {
    let newErrors = { podNumber: "", deliveryDate: "", deliveredBy: "" };

    // POD, Delivery Date, Delivered By required only if status is Shipped
    if (orderStatus === "Shipped") {
      if (!podNumber?.trim()) {
        newErrors.podNumber = "POD Number is required";
      }
      if (!deliveryDate?.trim()) {
        newErrors.deliveryDate = "Delivery Date is required";
      }
      if (!deliveredBy?.trim()) {
        newErrors.deliveredBy = "Delivered By is required";
      }

      if (Object.values(newErrors).some(error => error !== "")) {
        setErrors(newErrors);
        return;
      }
    }

    setErrors({ podNumber: "", deliveryDate: "", deliveredBy: "" });

    try {
      let formData;

      if (courierReceipt) {
        formData = new FormData();
        formData.append("order_id", selectedRow.id);
        formData.append("order_status", orderStatus || "Pending");
        formData.append("payment_status", paymentStatus || "Unpaid");
        formData.append("pod_number", podNumber?.trim() || "");
        formData.append("delivery_date", deliveryDate?.trim() || "");
        formData.append("delivered_by", deliveredBy?.trim() || "");
        formData.append("courier_receipt", courierReceipt);
      } else {
        formData = {
          order_id: selectedRow.id,
          order_status: orderStatus || "Pending",
          payment_status: paymentStatus || "Unpaid",
          pod_number: podNumber?.trim() || "",
          delivery_date: deliveryDate?.trim() || "",
          delivered_by: deliveredBy?.trim() || "",
        };
      }

      const response = await api.post("/order_details/update", formData, {
        headers: {
          "Content-Type": courierReceipt ? "multipart/form-data" : "application/json",
        },
      });

      if (!response || response.status !== 200) {
        throw new Error(`Failed to update order. Status: ${response?.status}`);
      }

      Swal.fire({
        title: "Order Updated Successfully!",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      });

      handleClose();
      fetchOrders();
    } catch (error) {
      console.error("Error updating order:", error);
      setSnackbar({
        open: true,
        message: "Failed to Update Order!",
        severity: "error",
      });
    }
  };
  const handleGenerateAndDownloadBill = async () => {
    setDownloadLoading(true);
    try {
      if (!selectedRow || !selectedRow.id) {
        setSnackbar({ open: true, message: "No order selected to generate bill.", severity: "warning" });
        return;
      }

      const orderItemsData = parseOrderItems(selectedRow.order_items);
      const productDetailsData = Array.isArray(selectedRow.product_details) ? selectedRow.product_details : [];

      let itemsHtml = '';
      let subtotal = 0;
      let totalDiscount = 0;
      let totalGST = 0;

      let totalCGST = 0;
      let totalSGST = 0;

      orderItemsData.forEach(item => {
        const productDetail = productDetailsData.find(pd => pd.product_id === item.product_id);

        const basePrice = parseFloat(productDetail?.price || 0);
        const quantity = parseInt(item.quantity || 1);

        const discountPercentage = parseFloat(productDetail?.discount || 0);
        const gstPercentage = parseFloat(productDetail?.gst_percentage || 0);

        const priceAfterDiscount = basePrice * (1 - discountPercentage / 100);
        const lineItemDiscountedTotal = priceAfterDiscount * quantity;

        let itemGSTAmount = 0;
        let itemCGST = 0;
        let itemSGST = 0;

        if (gstPercentage > 0) {
          itemGSTAmount = lineItemDiscountedTotal * (gstPercentage / 100);
          itemCGST = itemGSTAmount / 2;
          itemSGST = itemGSTAmount / 2;
        }

        subtotal += lineItemDiscountedTotal;
        totalCGST += itemCGST;
        totalSGST += itemSGST;
        totalDiscount += (basePrice * quantity) - lineItemDiscountedTotal;

        itemsHtml += `
    <tr>
      <td>${item.product_name}
      ${productDetail?.warranty_description
            ? `<br><small><strong>Warranty:</strong> ${productDetail.warranty_description}</small>`
            : ''
          }</td>
      <td>${quantity}</td>
      <td>₹${basePrice.toFixed(2)}</td>
      <td>₹${lineItemDiscountedTotal.toFixed(2)}</td>
      <td>${(gstPercentage / 2).toFixed(1)}% CGST<br>${(gstPercentage / 2).toFixed(1)}% SGST</td>
    </tr>
  `;
      });



      function numberToWords(num) {
        const ones = [
          "", "One", "Two", "Three", "Four", "Five", "Six",
          "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve",
          "Thirteen", "Fourteen", "Fifteen", "Sixteen",
          "Seventeen", "Eighteen", "Nineteen"
        ];
        const tens = [
          "", "", "Twenty", "Thirty", "Forty", "Fifty",
          "Sixty", "Seventy", "Eighty", "Ninety"
        ];

        const numToWords = (n) => {
          if (n < 20) return ones[n];
          if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
          if (n < 1000)
            return (
              ones[Math.floor(n / 100)] +
              " Hundred" +
              (n % 100 ? " and " + numToWords(n % 100) : "")
            );
          if (n < 100000)
            return (
              numToWords(Math.floor(n / 1000)) +
              " Thousand" +
              (n % 1000 ? " " + numToWords(n % 1000) : "")
            );
          if (n < 10000000)
            return (
              numToWords(Math.floor(n / 100000)) +
              " Lakh" +
              (n % 100000 ? " " + numToWords(n % 100000) : "")
            );
          return (
            numToWords(Math.floor(n / 10000000)) +
            " Crore" +
            (n % 10000000 ? " " + numToWords(n % 10000000) : "")
          );
        };

        return num === 0 ? "Zero" : numToWords(num);
      }
      const grandTotal = (subtotal - totalGST) + totalGST;
      const grandTotalWords = numberToWords(Math.floor(grandTotal)) + " Rupees Only";

      const vendorLogo =
        vendorDetails.logo_photo ||
        vendorDetails.logo ||
        vendorDetails.company_logo ||
        "";

      const billHtml = `
<html>
<head>
<title>Invoice #${selectedRow.id}</title>

<style>
  body {
    font-family: Arial, sans-serif;
    margin: 0;
    padding: 20px;
    font-size: 14px;
    background: #fff;
    color: #333;
  }

  .container {
    width: 100%;
    max-width: 800px;
    margin: auto;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: #fff;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  }

  .header {
    background-color: #f8f8f8;
    padding: 16px 24px;
    border-bottom: 1px solid #ddd;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .company-info h1 {
    margin: 0;
    color: #1a73e8;
    font-size: 24px;
    font-weight: bold;
  }

  .company-info p {
    margin: 2px 0;
    font-size: 12px;
    color: #555;
  }

  .company-logo img {
    width: 80px;
    height: 80px;
    object-fit: contain;
    background: transparent;
    border: none;
  }

  .invoice-title {
    font-size: 26px;
    font-weight: bold;
    color: #333;
    text-align: center;
    padding: 10px 0;
    background: #E8F1FF;
    border-bottom: 2px solid #1a73e8;
  }

  .details-section {
    display: flex;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid #eee;
  }

  .details-block {
    width: 48%;
  }

  .details-block h3 {
    font-size: 14px;
    margin-bottom: 6px;
    color: #1a73e8;
    font-weight: bold;
  }

  .details-block p {
    margin: 3px 0;
    font-size: 12px;
  }

  table {
    width: 90%;
    margin: 20px auto;
    border-collapse: collapse;
  }

  table th, table td {
    padding: 8px;
    border: 1px solid #eee;
    font-size: 12px;
    text-align: left;
  }

  table th {
    background-color: #f2f2f2;
    color: #1a73e8;
    font-weight: bold;
  }

  .totals-section {
    padding: 12px 24px;
    border-top: 1px solid #ddd;
    background-color: #f8f8f8;
  }

  .totals-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 6px;
    font-size: 13px;
  }

  .totals-label {
    font-weight: bold;
    margin-right: 10px;
    width: 120px;
    text-align: right;
  }

  .grand-total {
    font-size: 18px;
    font-weight: bold;
    color: #1a73e8;
    border-top: 2px solid #1a73e8;
    padding-top: 10px;
    text-align: right;
  }

  .amount-words {
    font-size: 12px;
    margin-top: 10px;
    font-weight: bold;
    color: #444;
    text-align: left;
  }

  .signature-section {
    display: flex;
    justify-content: flex-end;
    padding: 20px 24px;
  }

  .signature-container {
    text-align: right;
    font-size: 12px;
  }

  .signature-container p {
    font-weight: bold;
    margin-bottom: 6px;
    color: #1a73e8;
  }

  .footer-section {
    text-align: center;
    padding: 12px;
    font-size: 11px;
    background: #E8F1FF;
    border-top: 1px dashed #ddd;
    color: #555;
  }
</style>

</head>
<body>

<div class="container">

  <div class="header">
    <div class="company-info">
      <h1>${vendorDetails.company_name || "Company Name"}</h1>
      <p>${vendorDetails.company_address || ""}</p>
      <p>Email: ${vendorDetails.company_email || ""}</p>
      <p>Phone: ${vendorDetails.company_phone_no || ""}</p>
      <p>GST NO: ${vendorDetails.company_gst_no || ""}</p>
    </div>

   ${
  vendorDetails.logo_photo
    ? `<div class="company-logo">
         <img src="${ServerBaseUrlForStaticImages}/vendor_register/media/${vendorDetails.logo_photo}" />
       </div>`
    : ``
}

  </div>

  <div class="invoice-title">TAX INVOICE</div>

  <div class="details-section">
    <div class="details-block">
      <h3>Bill To:</h3>
      <p><strong>${selectedRow.customer_name}</strong></p>
      <p>Customer ID: ${selectedRow.customer_id}</p>
      <p>${selectedRow.billing_address}</p>
    </div>
    <div class="details-block">
      <h3>Order Info:</h3>
      <p>Date: ${formatDateForDisplay(selectedRow.order_date)}</p>
      <p>Invoice No: #${selectedRow.id}</p>
      ${selectedRow.reference_number ? `<p>Ref No: ${selectedRow.reference_number}</p>` : ""}
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>Product</th>
        <th>Qty</th>
        <th>Unit Price</th>
        <th>Discount Price</th>
        <th>GST</th>
      </tr>
    </thead>
    <tbody>${itemsHtml}</tbody>
  </table>

  <div class="totals-section">
    <div class="totals-row"><span class="totals-label">Subtotal:</span> ₹${(subtotal - totalCGST - totalSGST).toFixed(2)}</div>
    <div class="totals-row"><span class="totals-label">CGST:</span> ₹${totalCGST.toFixed(2)}</div>
    <div class="totals-row"><span class="totals-label">SGST:</span> ₹${totalSGST.toFixed(2)}</div>

    <div class="grand-total">Grand Total: ₹${grandTotal.toFixed(2)}</div>

    <div class="amount-words">Amount in Words: ${grandTotalWords}</div>
  </div>

  <div class="signature-section">
    <div class="signature-container">
      <p>Authorized Signature</p>
      ${vendorDetails.signature_photo
          ? `<img src="${ServerBaseUrlForStaticImages}/vendor_register/media/${vendorDetails.signature_photo}" style="width:140px;height:70px;object-fit:contain;" />`
          : `<p>______________</p>`
        }
    </div>
  </div>

  <div class="footer-section">
    Thank you for your order!
  </div>

</div>

<script>
window.onload = () => window.print();
</script>

</body>
</html>
`;



      // Open invoice in new tab & auto trigger print dialog
      const printWindow = window.open("", "_blank");
      printWindow.document.write(billHtml);
      printWindow.document.close();

      setSnackbar({ open: true, message: "Bill opened for print/save as PDF!", severity: "success" });
    } catch (error) {
      console.error("Error generating bill:", error);
      setSnackbar({ open: true, message: "Failed to generate bill. Please try again.", severity: "error" });
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleConfirmReject = async (event) => {
    if (event && event.preventDefault) {
      event.preventDefault();
    }

    if (!isCheckboxChecked) {
      setSnackbar({ open: true, message: "Please confirm by checking the checkbox.", severity: "warning" });
      return;
    }

    setLoading(true);

    try {
      const response = await api.delete(`/order_details/delete/${deleteOrderId}`);

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          text: "Order successfully rejected and deleted.",
          timer: 2000,
          showConfirmButton: false,
        });
        handleClose();
        fetchOrders();
      } else {
        Swal.fire({
          icon: "error",
          text: response.data.message || "Problem rejecting order. Please try again.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Error rejecting order:", error);
      Swal.fire({
        icon: "error",
        text: "An unexpected error occurred while rejecting the order. Please try again.",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
      setIsCheckboxChecked(false);
      setCustomerMessage("");
    }
  };

  const handleClose = () => {
    setOpenRejectModal(false);
    setOpenContentModal(false);
    setOpenZoomModal(false);
    setCourierReceipt(null);
    setErrors({ podNumber: "", deliveryDate: "", deliveredBy: "" });
    setOrderStatus("");
    setPaymentStatus("");
    setPodNumber("");
    setDeliveredBy("");
    setDeliveryDate("");
    setSelectedRow(null);
  };

  // Pagination Handlers
  const handleRowsPerPageChange = (event, newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when rows per page changes
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  // Calculate paginated data
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedOrders = orders.slice(startIndex, endIndex);


  // Data mapping for DataTable rows
  const data = Array.isArray(paginatedOrders) // Use paginatedOrders here
    ? paginatedOrders.map((order, index) => {
      // The order_date and order_items will be formatted by the accessor functions directly in the columns array.
      // We just need to ensure the raw data is passed correctly here.
      return {
        s_no: startIndex + index + 1, // Calculate serial number based on global index
        customer_name: order?.customer_name || "N/A",
        // 'order_date' and 'order_items' will be accessed and formatted by column accessors
        order_date: order.order_date, // Pass raw string, column accessor formats it
        order_items: order.order_items, // Pass raw string, column accessor parses and formats it
        total_amount: order?.total_amount || 0,
        payment_status: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {order.payment_status === "Pending" && (
              <Tooltip title="Payment Processing" arrow>
                <IconButton color="warning" size="small" style={{ marginLeft: "5px" }}>
                  <AccessTimeRounded />
                </IconButton>
              </Tooltip>
            )}
            {order.payment_status === "Paid" && (
              <Tooltip title="Payment Paid" arrow>
                <IconButton color="success" size="small" style={{ marginLeft: "5px" }}>
                  <CheckBoxRounded />
                </IconButton>
              </Tooltip>
            )}
            {order.payment_status === "Unpaid" && (
              <Tooltip title="Payment Unpaid" arrow>
                <IconButton color="error" size="small" style={{ marginLeft: "5px" }}>
                  <WarningIcon />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
        order_status: (
          <div style={{ display: "flex", alignItems: "center" }}>
            {order.order_status === "Delivered" && (
              <Tooltip title="Order Delivered" arrow>
                <IconButton color="success" size="small" style={{ marginLeft: "5px" }}>
                  <CheckBoxRounded />
                </IconButton>
              </Tooltip>
            )}
            {order.order_status === "Rejected" && (
              <Tooltip title="Order Rejected" arrow>
                <IconButton color="error" size="small" style={{ marginLeft: "5px" }}>
                  <Block />
                </IconButton>
              </Tooltip>
            )}
            {order.order_status === "Pending" && (
              <Tooltip title="Order Pending" arrow>
                <IconButton color="warning" size="small" style={{ marginLeft: "5px" }}>
                  <AccessTimeRounded />
                </IconButton>
              </Tooltip>
            )}
            {order.order_status === "Shipped" && (
              <Tooltip title="Order Shipped" arrow>
                <IconButton color="info" size="small" style={{ marginLeft: "5px" }}>
                  <LocalShipping />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ),
        option: (
          <div style={{ display: "flex" }}>
            {order.order_status === "Pending" && (
              <Tooltip title="Reject Order" arrow>
                <IconButton
                  color="error"
                  size="small"
                  style={{ marginLeft: "5px" }}
                  onClick={() => {
                    setDeleteOrderId(order.id);
                    setOpenRejectModal(true);
                  }}
                >
                  <Cancel />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="View Details / Update Order" arrow>
              <IconButton
                color="warning"
                size="small"
                style={{ marginLeft: "5px" }}
                onClick={() => {
                  setSelectedRow(order);
                  setOpenContentModal(true);
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </div>
        ),
        ...order, // Spread all original order properties for selectedRow to access them
      };
    })
    : [];


  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Order Details
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: columns, rows: data }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: rowsPerPage, entries: [5, 10, 15, 20, 25] }}
                    showTotalEntries={true}
                    canSearch={true}
                    noEndBorder
                    pagination={{
                      variant: "contained",
                      color: "dark",
                      page: page,
                      onPageChange: handlePageChange,
                      rowsPerPage: rowsPerPage,
                      onRowsPerPageChange: handleRowsPerPageChange,
                      count: orders.length, // Total number of orders for pagination count
                    }}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Reject Order Modal */}
      <Dialog open={openRejectModal} onClose={handleClose}>
        <MDBox component="form" onSubmit={handleConfirmReject}>
          <DialogTitle>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <WarningIcon style={{ color: red[700], fontSize: 40 }} />
              </Grid>
              <Grid item>
                <MDTypography variant="h6" component="span">
                  Confirm Reject Order
                </MDTypography>
              </Grid>
            </Grid>
          </DialogTitle>
          <DialogContent>
            <MDBox p={2} style={{ backgroundColor: red[50], borderLeft: `5px solid ${red[700]}` }}>
              <MDTypography variant="body1" gutterBottom>
                Are you sure you want to reject this order? This action cannot be undone.
              </MDTypography>
              <TextField
                fullWidth
                label="Message to Customer"
                variant="outlined"
                multiline
                rows={3}
                value={customerMessage}
                onChange={(e) => setCustomerMessage(e.target.value)}
                sx={{ mt: 2 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCheckboxChecked}
                    onChange={(e) => setIsCheckboxChecked(e.target.checked)}
                  />
                }
                label="I confirm to send the message and reject this order."
                sx={{ mt: 1 }}
              />
            </MDBox>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} style={{ color: 'gray' }} disabled={loading}>Cancel</Button>
            <MDButton
              type="submit"
              disabled={!isCheckboxChecked || loading}
              color="primary"
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : "Reject Order"}
            </MDButton>
          </DialogActions>
        </MDBox>
      </Dialog>

      {/* Order Details and Update Modal */}
      {selectedRow && (
        <Dialog open={openContentModal} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle
            sx={{
              bgcolor: "#1976D2",
              color: "#FFF",
              fontWeight: "bold",
              textAlign: "center",
              fontSize: "1.5rem",
              letterSpacing: 1,
            }}
          >
            Order Details
          </DialogTitle>

          <DialogContent dividers sx={{ bgcolor: "#F4F6F8", p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={3} sx={{ p: 2, borderRadius: 3, backgroundColor: "#E3F2FD", fontSize: "14px" }}>
                  <MDTypography variant="subtitle2" mb={1}><strong>Customer:</strong> {selectedRow.customer_name}</MDTypography>
                  <MDTypography variant="subtitle2" mb={1}><strong>Customer ID:</strong> {selectedRow.customer_id}</MDTypography>
                  <MDTypography variant="subtitle2" mb={1}><strong>Order Date:</strong> {formatDateForDisplay(selectedRow.order_date)}</MDTypography>
                  <MDTypography variant="subtitle2" mb={1}><strong>Shipping Address:</strong> {selectedRow.shipping_address}</MDTypography>
                  <MDTypography variant="subtitle2"><strong>Billing Address:</strong> {selectedRow.billing_address}</MDTypography>
                  {selectedRow.reference_number && <MDTypography variant="subtitle2"><strong>Reference Number:</strong> {selectedRow.reference_number}</MDTypography>}
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card elevation={3} sx={{ p: 2, borderRadius: 3, backgroundColor: "#F3E5F5", fontSize: "14px" }}>
                  <MDTypography variant="subtitle2" mb={1}>
                    <strong>Total Amount:</strong> <span style={{ color: "#2E7D32", fontWeight: "bold" }}>₹{selectedRow.total_amount}</span>
                  </MDTypography>
                  <MDTypography variant="subtitle2" mb={1}>
                    <strong>Payment Type:</strong> <span>{selectedRow.payment_type}</span>
                  </MDTypography>
                  <MDTypography variant="subtitle2" mb={1}>
                    <strong>Payment Status:</strong> <span style={{ color: selectedRow.payment_status === "Paid" ? "#2E7D32" : "#D32F2F", fontWeight: "bold" }}>{selectedRow.payment_status}</span>
                  </MDTypography>
                  <MDTypography variant="subtitle2" mb={1}>
                    <strong>Order Status:</strong> <span style={{ color: selectedRow.order_status === "Delivered" ? "#2E7D32" : selectedRow.order_status === "Pending" ? "#FFA000" : selectedRow.order_status === "Shipped" ? "#0288D1" : "#D32F2F", fontWeight: "bold" }}>{selectedRow.order_status}</span>
                  </MDTypography>
                  <MDTypography variant="subtitle2">
                    <strong>Delivered By:</strong> {selectedRow.delivered_by || "Not Assigned"}
                  </MDTypography>
                </Card>
              </Grid>
            </Grid>

            {selectedRow?.screenshot && (
              <Card
                elevation={3}
                sx={{
                  p: 2, mt: 3, borderRadius: 3, backgroundColor: "#FFFDE7", textAlign: "center",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  transition: "0.3s", "&:hover": { transform: "scale(1.02)", boxShadow: 6, },
                }}
              >
                <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
                  Payment Screenshot:
                </MDTypography>
                <Box
                  component="img"
                  src={`${ServerBaseUrlForStaticImages}/order_details/view/payment/${selectedRow.screenshot}`}
                  alt="Payment Screenshot"
                  sx={{
                    width: "100%", maxWidth: "300px", height: "200px", objectFit: "contain",
                    borderRadius: 2, cursor: "pointer", backgroundColor: "#fff", p: 1, boxShadow: 2,
                    transition: "0.3s", "&:hover": { transform: "scale(1.02)", boxShadow: 6, },
                  }}
                  onClick={() => {
                    setZoomImageUrl(`${ServerBaseUrlForStaticImages}/order_details/view/payment/${selectedRow.screenshot}`);
                    setOpenZoomModal(true);
                  }}
                />
              </Card>
            )}

            {selectedRow?.product_details?.length > 0 && (
              <>
                <MDTypography variant="h6" fontWeight="bold" mt={4} mb={2}>
                  Product Details
                </MDTypography>

                <Grid container spacing={2}>
                  {selectedRow.product_details.map((product, index) => {
                    const parsedProductImages = typeof product.product_images === 'string'
                      ? JSON.parse(product.product_images)
                      : product.product_images;
                    const productImageUrl = Array.isArray(parsedProductImages) && parsedProductImages.length > 0
                      ? `${ServerBaseUrlForStaticImages}/product/media/${parsedProductImages[0]}`
                      : null;

                    let productQuantity = 'N/A';
                    const orderItemsInModal = parseOrderItems(selectedRow.order_items);
                    const relevantOrderItem = orderItemsInModal.find(item => item.product_id === product.product_id);
                    productQuantity = relevantOrderItem ? relevantOrderItem.quantity : 'N/A';

                    return (
                      <Grid item xs={12} md={6} key={index}>
                        <Card elevation={5} sx={{
                          p: 2, borderRadius: 3, minHeight: "320px", backgroundColor: "#E8F5E9",
                          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                          transition: "0.3s", "&:hover": { transform: "scale(1.02)", boxShadow: 8, }, fontSize: "14px",
                        }}>
                          <div>
                            <MDTypography variant="subtitle2" mb={1}><strong>Product Name:</strong> {product.product_name}</MDTypography>
                            <MDTypography variant="subtitle2" mb={1}><strong>Quantity:</strong> {productQuantity}</MDTypography>
                            <MDTypography variant="subtitle2" mb={1}>
                              <strong>Price:</strong>{" "}
                              {product.discount ? (
                                <>
                                  <span style={{ textDecoration: "line-through", color: "gray" }}> ₹{product.price}</span>{" "}
                                  <span style={{ color: "green", fontWeight: "bold" }}>
                                    ₹{(product.price * (100 - parseFloat(product.discount)) / 100).toFixed(2)}
                                  </span>
                                </>
                              ) : (
                                <> ₹{product.price} </>
                              )}
                            </MDTypography>
                            {product.discount && (
                              <MDTypography variant="subtitle2" mb={1}>
                                <strong>Discount:</strong> {product.discount}% OFF
                              </MDTypography>
                            )}
                            <MDTypography variant="subtitle2"><strong>SKU:</strong> {product.sku || "N/A"}</MDTypography>
                          </div>
                          {productImageUrl && (
                            <Box
                              component="img"
                              src={productImageUrl}
                              alt="Product"
                              sx={{
                                mt: 2, width: "100%", height: "160px", objectFit: "contain",
                                borderRadius: 2, backgroundColor: "#fff", cursor: "pointer",
                                transition: "0.3s", "&:hover": { transform: "scale(1.02)", boxShadow: 6, },
                              }}
                              onClick={() => {
                                setZoomImageUrl(productImageUrl);
                                setOpenZoomModal(true);
                              }}
                            />
                          )}
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            <Paper elevation={3} sx={{ p: 3, mt: 3, borderRadius: 3, backgroundColor: "#FFF3E0" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth> {/* Removed disabled logic here */}
                    <InputLabel>Order Status</InputLabel>
                    <Select
                      value={orderStatus}
                      onChange={(e) => setOrderStatus(e.target.value)}
                      sx={{ height: 42 }}
                      label="Order Status"
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Shipped">Shipped</MenuItem>
                      {/* 'Delivered' removed from here as per new requirement */}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth> {/* Payment status can be updated at any time */}
                    <InputLabel>Payment Status</InputLabel>
                    <Select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                      sx={{ height: 42 }}
                      label="Payment Status"
                    >
                      <MenuItem value="Paid">Paid</MenuItem>
                      <MenuItem value="Unpaid">Unpaid</MenuItem>
                      <MenuItem value="Pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Conditional fields now active only when orderStatus is 'Shipped' */}
                {orderStatus === "Shipped" && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="POD Number"
                        variant="outlined"
                        value={podNumber}
                        onChange={(e) => setPodNumber(e.target.value)}
                        error={!!errors.podNumber}
                        helperText={errors.podNumber}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Delivery Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        value={deliveryDate}
                        onChange={(e) => setDeliveryDate(e.target.value)}
                        error={!!errors.deliveryDate}
                        helperText={errors.deliveryDate}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Delivered By"
                        variant="outlined"
                        value={deliveredBy}
                        onChange={(e) => setDeliveredBy(e.target.value)}
                        error={!!errors.deliveredBy}
                        helperText={errors.deliveredBy}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 2, textAlign: "center", backgroundColor: "#FFF3E0", borderRadius: 3,
                          height: "100%", border: "2px dashed #FF9800", display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center", transition: "0.3s",
                          "&:hover": { borderColor: "#FB8C00", },
                        }}
                      >
                        <label style={{ fontWeight: "bold", marginBottom: 12, fontSize: "15px", color: "#E65100" }}>
                          Upload Courier Receipt
                        </label>
                        <input
                          type="file"
                          accept="image/png, image/jpeg, image/jpg, image/webp"
                          style={{ display: "none" }}
                          id="upload-courier-receipt"
                          onChange={(e) => {
                            if (e.target.files.length > 0) {
                              setCourierReceipt(e.target.files[0]);
                            }
                          }}
                        />
                        <label htmlFor="upload-courier-receipt">
                          <Button
                            variant="contained" component="span" sx={{
                              mt: 1, mb: 2, backgroundColor: "#FB8C00", color: "#fff",
                              "&:hover": { backgroundColor: "#F57C00" }, textTransform: "none", fontWeight: "bold",
                            }}
                          >
                            Choose File
                          </Button>
                        </label>

                        {courierReceipt ? (
                          <img
                            src={URL.createObjectURL(courierReceipt)}
                            alt="New Courier Receipt Preview"
                            style={{
                              width: "100px", height: "100px", borderRadius: 8, objectFit: "cover",
                              marginTop: 8, cursor: "pointer", transition: "0.3s", "&:hover": { transform: "scale(1.1)" },
                            }}
                            onClick={() => {
                              setZoomImageUrl(URL.createObjectURL(courierReceipt));
                              setOpenZoomModal(true);
                            }}
                          />
                        ) : selectedRow?.courier_receipt ? (
                          <img
                            src={`${ServerBaseUrlForStaticImages}/order_details/view/courier/${selectedRow.courier_receipt}`}
                            alt="Existing Courier Receipt"
                            style={{
                              width: "100px", height: "100px", borderRadius: 8, objectFit: "cover",
                              marginTop: 8, cursor: "pointer", transition: "0.3s", "&:hover": { transform: "scale(1.1)" },
                            }}
                            onClick={() => {
                              setZoomImageUrl(`${ServerBaseUrlForStaticImages}/order_details/view/courier/${selectedRow.courier_receipt}`);
                              setOpenZoomModal(true);
                            }}
                          />
                        ) : (
                          <MDTypography variant="caption" mt={2} color="textSecondary">
                            No file selected
                          </MDTypography>
                        )}
                      </Paper>
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          </DialogContent>

          <DialogActions sx={{ bgcolor: "#E0F7FA", px: 3, py: 2 }}>
            <MDButton
              onClick={handleUpdateOrder}
              color="primary"
              variant="contained"
              sx={{ fontWeight: "bold", px: 3 }}
              disabled={
                // Disable if there are any errors in the form fields that become required when shipped
                (orderStatus === "Shipped" && (
                  !!errors.podNumber ||
                  !!errors.deliveryDate ||
                  !!errors.deliveredBy ||
                  !podNumber?.trim() || // Also check if empty
                  !deliveryDate?.trim() || // Also check if empty
                  !deliveredBy?.trim() // Also check if empty
                ))
              }
            >
              Update Order
            </MDButton>
            <MDButton
              onClick={handleGenerateAndDownloadBill}
              color="info"
              variant="contained"
              startIcon={downloadLoading ? <CircularProgress size={20} color="inherit" /> : <Download />}
              sx={{ fontWeight: "bold", px: 3 }}
              disabled={!selectedRow.id || downloadLoading}
            >
              {downloadLoading ? "Generating..." : "Download Bill"}
            </MDButton>
            <MDButton
              onClick={handleClose}
              color="secondary"
              variant="contained"
              sx={{ fontWeight: "bold", px: 3 }}
            >
              Close
            </MDButton>
          </DialogActions>
        </Dialog>
      )}

      {/* Image Zoom Modal */}
      <Dialog open={openZoomModal} onClose={() => setOpenZoomModal(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, bgcolor: "#000" }}>
          <img
            src={zoomImageUrl}
            alt="Zoomed Screenshot"
            style={{
              width: "100%", height: "auto", objectFit: "contain", backgroundColor: "#000",
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <MuiAlert elevation={6} variant="filled" severity={snackbar.severity}>
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default OrderDetails;